export function getTargetScrollLeftPx(activeIndex: number | null, slotPx: number): number {
    if (slotPx <= 0) return 0;
    return (activeIndex ?? 0) * slotPx;
}

function evaluateCssEase(progress: number): number {
    if (progress <= 0) return 0;
    if (progress >= 1) return 1;

    const x1 = 0.25;
    const y1 = 0.1;
    const x2 = 0.25;
    const y2 = 1;

    const bezier = (t: number, p1: number, p2: number) => {
        const u = 1 - t;
        return 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t;
    };

    let lo = 0;
    let hi = 1;
    let t = progress;
    for (let i = 0; i < 20; i += 1) {
        const x = bezier(t, x1, x2);
        if (Math.abs(x - progress) < 1e-5) break;
        if (x < progress) lo = t;
        else hi = t;
        t = (lo + hi) / 2;
    }

    return bezier(t, y1, y2);
}

export function animateScrollLeft(
    element: HTMLElement,
    targetLeft: number,
    durationMs: number
): () => void {
    const startLeft = element.scrollLeft;
    const delta = targetLeft - startLeft;

    if (Math.abs(delta) < 0.5) {
        return () => {};
    }

    const startTime = performance.now();
    let rafId = 0;
    let cancelled = false;
    let expectedScrollLeft = startLeft;

    const step = (now: number) => {
        if (cancelled) return;

        if (Math.abs(element.scrollLeft - expectedScrollLeft) > 1) {
            return;
        }

        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / durationMs);
        // eslint-disable-next-line no-param-reassign
        element.scrollLeft = startLeft + delta * evaluateCssEase(progress);
        expectedScrollLeft = element.scrollLeft;

        if (progress < 1) {
            rafId = requestAnimationFrame(step);
        }
    };

    rafId = requestAnimationFrame(step);

    return () => {
        cancelled = true;
        cancelAnimationFrame(rafId);
    };
}

export type AnimatedCarouselMode =
    | 'normal'
    | 'active'
    | 'activeInPlace'
    | 'activeShiftedRight'
    | 'translatedDefault'
    | 'shiftedRight';

type AnimatedCarouselDirection = 'left' | 'right';

type AnimatedCarouselItemOffset = -1 | 0 | 1;
type AnimatedCarouselItemContent = 'default' | 'active';

export interface AnimatedCarouselItemState {
    offset: AnimatedCarouselItemOffset;
    content: AnimatedCarouselItemContent;
}

export function createInitialItemStates(count: number): AnimatedCarouselItemState[] {
    return Array.from({ length: count }, () => ({ offset: 0, content: 'default' }));
}

export function getModeFromItemState(state: AnimatedCarouselItemState): AnimatedCarouselMode {
    if (state.content === 'active') {
        if (state.offset === -1) return 'active';
        if (state.offset === 1) return 'activeShiftedRight';
        return 'activeInPlace';
    }
    if (state.offset === -1) return 'translatedDefault';
    if (state.offset === 1) return 'shiftedRight';
    return 'normal';
}

function clampOffset(value: number): AnimatedCarouselItemOffset {
    if (value <= -1) return -1;
    if (value >= 1) return 1;
    return 0;
}

interface ApplyClickOptions {
    states: AnimatedCarouselItemState[];
    activeIndex: number | null;
    clickedIndex: number;
}

interface ApplyClickResult {
    states: AnimatedCarouselItemState[];
    activeIndex: number;
}

export function applyAnimatedCarouselClick({
    states,
    activeIndex,
    clickedIndex,
}: ApplyClickOptions): ApplyClickResult | null {
    if (clickedIndex === activeIndex) {
        return null;
    }

    const next = states.slice();

    if (activeIndex === null) {
        next[clickedIndex] = { offset: 0, content: 'active' };
        for (let i = clickedIndex + 1; i < next.length; i++) {
            next[i] = { offset: 1, content: 'default' };
        }
        return { states: next, activeIndex: clickedIndex };
    }

    const direction: AnimatedCarouselDirection = clickedIndex > activeIndex ? 'right' : 'left';
    const shift = direction === 'right' ? -1 : 1;

    const min = Math.min(activeIndex, clickedIndex);
    const max = Math.max(activeIndex, clickedIndex);
    for (let i = min + 1; i <= max; i++) {
        const nextItem = next[i];

        if (nextItem) {
            const newOffset = clampOffset(nextItem.offset + shift);
            next[i] = { offset: newOffset, content: 'default' };
        }
    }

    const nextActiveItem = next[activeIndex];

    if (direction === 'right' && nextActiveItem) {
        next[activeIndex] = { ...nextActiveItem, content: 'default' };
    }

    next[clickedIndex] = { offset: 0, content: 'active' };

    return { states: next, activeIndex: clickedIndex };
}
